// Which rail a RechargeModal "Recharge / Add Credits" click takes.
//
// Extracted because the answer is a PRECEDENCE, and precedence is what went
// wrong: the trial branch (`isTrial && onUpgrade` → /change-plan) sat above the
// Razorpay-saved-card branch, so a free-trial account whose only card was
// Razorpay-vaulted was sent to /change-plan — which charges by card_mapping_id
// through the card's own gateway and cannot authenticate a Razorpay token. That
// account had no working upgrade path at all, while the same account paying by
// netbanking worked, because netbanking is checked first.
//
// The rule (AGENTS.md failure mode 20): decide on what the payment CAN be
// executed by before any convenience short-circuit. Gateway capability is not a
// convenience — a Razorpay token is chargeable on exactly one rail — so it
// outranks the plan-upgrade shortcut. The upgrade still happens: the Razorpay
// order carries `upgrade_plan` (see ../upgrade-plan.ts) and payment-service
// flips the plan from the capture webhook, the same asynchronous shape
// netbanking has always used.
export type RechargeRoute =
  // Cashfree-hosted netbanking page. Razorpay has no netbanking, so this is
  // cross-gateway by design and carries upgrade_plan on its Cashfree order.
  | 'netbanking'
  // Hosted Razorpay Standard Checkout. Plan flip rides on the order.
  | 'razorpay-checkout'
  // POST /change-plan — charges AND upgrades synchronously. Stripe/Cashfree cards only.
  | 'change-plan'
  // POST /recharge/:card_mapping_id — a paid account topping up.
  | 'recharge'

export interface RechargeRouteInput {
  method: 'card' | 'netbanking'
  // The selected saved card is Razorpay-tokenised (isRazorpaySavedCard).
  isRazorpayCard: boolean
  isTrial: boolean
  // The host wired an onUpgrade handler. Only /change-plan needs it — the two
  // gateway rails carry the upgrade in their own payload, so they must not be
  // gated on a prop the host may not have passed.
  hasUpgradeHandler: boolean
}

export const resolveRechargeRoute = ({
  method,
  isRazorpayCard,
  isTrial,
  hasUpgradeHandler,
}: RechargeRouteInput): RechargeRoute => {
  if (method === 'netbanking') return 'netbanking'
  if (isRazorpayCard) return 'razorpay-checkout'
  if (isTrial && hasUpgradeHandler) return 'change-plan'
  return 'recharge'
}
